import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui';
import { Card } from '../ui';
import ForgotPasswordModal from './ForgotPasswordModal';
import { 
  Mail, 
  Lock, 
  ArrowRight, 
  ArrowLeft, 
  Eye, 
  EyeOff,
  Layers,
  AlertCircle,
  Home,
  Languages
} from 'lucide-react';

const LoginForm = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { isRTL, t, language, toggleLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showSignupOption, setShowSignupOption] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(formData.email.trim().toLowerCase(), formData.password.trim());

    if (result.success) {
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      }
      navigate('/dashboard');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // If showing signup option, show choice buttons
  if (showSignupOption) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-white to-emerald-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden" 
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Background Patterns */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-0 w-96 h-96 bg-sky-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-700"></div>
        </div>

        <div className="max-w-md w-full space-y-6 relative z-10 animate-in fade-in duration-500">
          {/* Language Toggle Button - Top Right */}
          <div className="flex justify-end">
            <button
              onClick={toggleLanguage}
              className="inline-flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-sky-600 border-2 border-slate-200 hover:border-sky-600 rounded-xl transition-all font-bold text-sm bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-md"
              title={isRTL ? 'Switch to English' : 'التبديل إلى العربية'}
            >
              <Languages className="w-4 h-4" />
              <span>{language === 'ar' ? 'EN' : 'عربي'}</span>
            </button>
          </div>

          <div className="text-center">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-sky-600 to-emerald-500 rounded-xl flex items-center justify-center text-white shadow-lg">
                <Layers size={24} />
              </div>
              <span className="text-2xl font-bold text-slate-900">
                {isRTL ? 'سَلِس' : 'sls'}<span className="text-sky-600">.</span>
              </span>
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">
              {t('welcomeToApp') || 'Welcome to sls'}
            </h1>
            <p className="text-slate-600 text-lg">
              {t('chooseOption') || 'Choose your account type'}
            </p>
          </div>

          <Card className="p-8 space-y-4 shadow-xl border-0 bg-white/80 backdrop-blur-sm">
            <Button
              variant="primary"
              size="lg"
              className="w-full bg-gradient-to-r from-sky-600 to-sky-700 hover:from-sky-700 hover:to-sky-800 text-white shadow-lg shadow-sky-500/30"
              onClick={() => navigate('/signup/agency')}
            >
              {t('createNewAgency') || 'Create New Agency'}
              {isRTL ? <ArrowLeft className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
            </Button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-500 font-medium">{t('or') || 'OR'}</span>
              </div>
            </div>

            <Button
              variant="secondary"
              size="lg"
              className="w-full border-2"
              onClick={() => setShowSignupOption(false)}
            >
              {t('signInExisting') || 'Sign in to Existing Account'}
            </Button>
          </Card>

          <p className="text-center text-sm text-slate-600">
            {t('alreadyHaveAccount') || 'Already have an account?'}{' '}
            <button
              onClick={() => setShowSignupOption(false)}
              className="font-bold text-sky-600 hover:text-sky-700 transition-colors"
            >
              {t('signIn') || 'Sign In'}
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-white to-emerald-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden" 
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Background Patterns */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 right-0 w-96 h-96 bg-sky-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-700"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-sky-100 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230ea5e9' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      ></div>

      <div className="max-w-md w-full space-y-8 relative z-10 animate-in fade-in duration-500">
        {/* Language Toggle Button - Top Right */}
        <div className="flex justify-end">
          <button
            onClick={toggleLanguage}
            className="inline-flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-sky-600 border-2 border-slate-200 hover:border-sky-600 rounded-xl transition-all font-bold text-sm bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-md"
            title={isRTL ? 'Switch to English' : 'التبديل إلى العربية'}
          >
            <Languages className="w-4 h-4" />
            <span>{language === 'ar' ? 'EN' : 'عربي'}</span>
          </button>
        </div>

        {/* Header with Logo */}
        <div className="text-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 mb-6 group"
          >
            <div className="w-14 h-14 bg-gradient-to-br from-sky-600 to-emerald-500 rounded-xl flex items-center justify-center text-white shadow-lg group-hover:shadow-sky-500/30 transition-all group-hover:scale-105">
              <Layers size={28} />
            </div>
            <span className="text-3xl font-bold text-slate-900 group-hover:text-sky-600 transition-colors">
              {isRTL ? 'سَلِس' : 'sls'}<span className="text-sky-600">.</span>
            </span>
          </Link>
          
          <h2 className="text-2xl font-semibold text-slate-700 mb-2">
            {t('signInToAccount') || 'Sign in to your account'}
          </h2>
          <p className="text-slate-600 text-sm">
            {t('contentApprovalSimple') || 'Welcome back! Please enter your details.'}
          </p>
        </div>

        {/* Main Form Card */}
        <Card className="p-8 shadow-2xl border-0 bg-white/90 backdrop-blur-sm">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Error Message */}
            {error && (
              <div className="rounded-xl bg-red-50 border-2 border-red-200 p-4 animate-in slide-in-from-top duration-300">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800 font-medium">{error}</p>
                </div>
              </div>
            )}

            {/* Email Field */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-bold text-slate-700">
                {t('emailAddress') || 'Email Address'}
              </label>
              <div className="relative">
                <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none`}>
                  <Mail className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                  placeholder={t('emailPlaceholder') || 'you@example.com'}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-bold text-slate-700">
                {t('password') || 'Password'}
              </label>
              <div className="relative">
                <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none`}>
                  <Lock className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full ${isRTL ? 'pl-12 pr-12' : 'pl-12 pr-12'} py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                  placeholder={t('passwordPlaceholder') || '••••••••'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-4' : 'right-0 pr-4'} flex items-center text-slate-400 hover:text-slate-600 transition-colors`}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center group cursor-pointer">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-slate-300 rounded cursor-pointer"
                />
                <label htmlFor="remember-me" className={`${isRTL ? 'mr-2' : 'ml-2'} block text-sm text-slate-700 font-medium cursor-pointer group-hover:text-slate-900 transition-colors`}>
                  {t('rememberMe') || 'Remember me'}
                </label>
              </div>

              <div className="text-sm">
                <button
                  type="button"
                  onClick={() => setShowForgotPasswordModal(true)}
                  className="font-bold text-sky-600 hover:text-sky-700 transition-colors"
                >
                  {t('forgotPassword') || 'Forgot password?'}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              className="w-full bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30 py-3 text-base font-bold rounded-xl transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              disabled={loading}
              loading={loading}
            >
              {loading ? (t('signingIn') || 'Signing in...') : (t('signIn') || 'Sign In')}
              {!loading && (isRTL ? <ArrowLeft className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />)}
            </Button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-500 font-medium">{t('or') || 'OR'}</span>
              </div>
            </div>

            {/* Signup Link */}
            <div className="text-center">
              <p className="text-sm text-slate-600">
                {t('dontHaveAccount') || "Don't have an account?"}{' '}
                <button
                  type="button"
                  onClick={() => setShowSignupOption(true)}
                  className="font-bold text-sky-600 hover:text-sky-700 transition-colors"
                >
                  {t('createNewAccount') || 'Create new account'}
                </button>
              </p>
            </div>
          </form>
        </Card>

        {/* Back to Home Link & Language Toggle */}
        <div className="flex items-center justify-between">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors font-medium"
          >
            <Home className="w-4 h-4" />
            {isRTL ? 'العودة للصفحة الرئيسية' : 'Back to Home'}
          </Link>
          
          <button
            onClick={toggleLanguage}
            className="inline-flex items-center gap-2 px-3 py-2 text-slate-600 hover:text-sky-600 border border-slate-200 hover:border-sky-600 rounded-lg transition-all text-sm font-medium"
            title={isRTL ? 'Switch to English' : 'التبديل إلى العربية'}
          >
            <Languages className="w-4 h-4" />
            <span>{language === 'ar' ? 'EN' : 'عربي'}</span>
          </button>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal 
        isOpen={showForgotPasswordModal} 
        onClose={() => setShowForgotPasswordModal(false)} 
      />
    </div>
  );
};

export default LoginForm;
